/**
 * The homepage sport cards' headline records: each sport's record since 2020 (the
 * betstamp-verified era), read live from the public release stats endpoint.
 *
 * Counting rules (the same as every public record): sides and totals, graded
 * results only (pushes kept in the record and the units), every period and game
 * type, units = the sum of each play's graded units. CFB (sportId 1) covers FBS
 * and FCS. The server caches these exact urls
 * (server/src/services/release/statsCacheUrls.js); keep the two in step.
 */

export const SINCE_2020_FROM = '2020-07-01';
export const SINCE_2020_LABEL = 'Since 2020 · verified at betstamp';

export const SPORT_RECORD_IDS = { nfl: 3, cfb: 1, cbb: 2 } as const;
export type SportRecordKey = keyof typeof SPORT_RECORD_IDS;

export const since2020StatsUrl = (sportId: number) =>
  `/api/v1/release/stats?data[sportId]=${sportId}&data[playType][]=side&data[playType][]=total&data[result][]=win&data[result][]=loss&data[result][]=push&data[gameTime$>=]=${SINCE_2020_FROM}`;

export interface SportRecord {
  win: number;
  loss: number;
  push: number;
  units: number;
}

/** The win/loss/push counts and units from a stats response, or undefined when unusable. */
export function sportRecordFromStats(stats?: Partial<API.ReleaseStats> | null) {
  const result = stats?.result;
  if (!result) {
    return undefined;
  }
  const count = (key: 'win' | 'loss' | 'push') => Number(result[key]?.total ?? 0);
  const units = (['win', 'loss', 'push'] as const).reduce(
    (sum, key) => sum + Number(result[key]?.units ?? 0),
    0,
  );
  const record: SportRecord = {
    win: count('win'),
    loss: count('loss'),
    push: count('push'),
    units,
  };
  const valid = Object.values(record).every(Number.isFinite) && record.win + record.loss > 0;
  return valid ? record : undefined;
}

/** Card copy: "229-185-7", "55.3%" (wins over wins plus losses), "+26.53u". */
export function formatSportRecord(record: SportRecord) {
  const winRate = (record.win / (record.win + record.loss)) * 100;
  const units = Math.round(record.units * 100) / 100;
  return {
    record: `${record.win}-${record.loss}-${record.push}`,
    winRate: `${winRate.toFixed(1)}%`,
    units: `${units >= 0 ? '+' : '-'}${Math.abs(units).toFixed(2)}u`,
  };
}
